import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { firstValueFrom } from 'rxjs';
import { TokenService } from './token.service';

const PREF_PHONE    = 'pax_pending_phone';
const PREF_OTP_STEP = 'pax_otp_step';

export interface PassengerUser {
  id: number;
  name: string | null;
  email: string | null;
  phoneNumber: string;
  balance: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http        = inject(HttpClient);
  private readonly tokenService = inject(TokenService);

  /** Reactive user — null = guest (not logged in) */
  readonly currentUser = signal<PassengerUser | null>(null);

  /** Must be called once at app startup before any other auth method. */
  async init(): Promise<void> {
    await this.tokenService.init();
  }

  async loadUserFromToken(): Promise<void> {
    if (!this.tokenService.isLoggedIn()) return;
    try {
      const user = await firstValueFrom(this.http.get<PassengerUser>('/auth/me'));
      this.currentUser.set({ ...user, balance: user.balance ?? 0 });
    } catch (err: any) {
      // Only wipe the token on a 401 (invalid/revoked token).
      // Network errors, 5xx, etc. should NOT log the user out.
      if (err?.status === 401) {
        await this.tokenService.clearToken();
      }
      // In all other error cases the user stays "logged in" in memory with
      // whatever data we already have (or null if first boot with no network).
    }
  }

  /** Re-fetches the user from the API and updates the signal (e.g. after a recharge). */
  async refreshUser(): Promise<void> {
    if (!this.tokenService.isLoggedIn()) return;
    try {
      const user = await firstValueFrom(this.http.get<PassengerUser>('/auth/me'));
      this.currentUser.set({ ...user, balance: user.balance ?? 0 });
    } catch { /* silently ignore */ }
  }

  requestOtp(phoneNumber: string) {
    return firstValueFrom(
      this.http.post('/auth/passenger/request-otp', { identifier: phoneNumber })
    );
  }

  async verifyOtp(phoneNumber: string, otpCode: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<{ access_token: string; user: any }>('/auth/passenger/verify-otp', {
        identifier: phoneNumber,
        otpCode,
      })
    );

    await this.tokenService.setToken(res.access_token);
    // Set basic info immediately so the UI isn't empty while we fetch
    this.currentUser.set({
      id:          res.user.id,
      name:        res.user.name,
      email:       res.user.email,
      phoneNumber,
      balance:     Number(res.user.balance ?? 0),
    });
    // Fetch the full profile (including accurate balance from DB) right after login
    await this.refreshUser();
    await this.clearOtpState();
  }

  async logout(): Promise<void> {
    await this.tokenService.clearToken();
    this.currentUser.set(null);
  }

  requestAccountDeletion(): Observable<any> {
    return this.http.post<any>('/auth/request-account-deletion', {});
  }

  deleteAccount(otpCode: string): Observable<any> {
    return this.http.delete<any>('/auth/account', { body: { otpCode } });
  }

  // ── OTP state persistence (survives Huawei MagicOS process kill) ────────────
  async saveOtpState(phone: string): Promise<void> {
    await Preferences.set({ key: PREF_PHONE,    value: phone });
    await Preferences.set({ key: PREF_OTP_STEP, value: 'otp' });
  }

  async restoreOtpState(): Promise<{ phone: string } | null> {
    const { value: phone } = await Preferences.get({ key: PREF_PHONE });
    const { value: step  } = await Preferences.get({ key: PREF_OTP_STEP });
    return phone && step === 'otp' ? { phone } : null;
  }

  async clearOtpState(): Promise<void> {
    await Preferences.remove({ key: PREF_PHONE });
    await Preferences.remove({ key: PREF_OTP_STEP });
  }
}
